import { useEffect, useRef } from "react";
import { useApi } from "@/hooks/core/useApi";
import { clientConfig } from "@/lib/config/client-config";
import { useAntiBotVerification } from "@/providers/AntiBotVerificationProvider";
import { create } from "zustand";

const REFRESH_CLEARANCE_TURNSTILE_SITE_KEY = clientConfig.refreshClearanceTurnstileSiteKey;

type RefreshAntibotClearanceStore = {
  isRefreshingClearance: boolean;
  setIsRefreshingClearance: (isRefreshingClearance: boolean) => void;
};

export const useRefreshAntibotClearance = create<RefreshAntibotClearanceStore>((set) => ({
  isRefreshingClearance: false,
  setIsRefreshingClearance: (isRefreshingClearance: boolean) => set({ isRefreshingClearance }),
}));

export const useRefreshAntibotClearanceSetup = () => {
  const api = useApi();
  const { renderTurnstileWidget } = useAntiBotVerification();
  const { setIsRefreshingClearance } = useRefreshAntibotClearance();

  const currentRefreshingClearancePromiseRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    const triggerRefreshClearance = async () => {
      setIsRefreshingClearance(true);
      const renderChallengeAndWaitForResolutionPromise = new Promise((resolve, reject) => {
        const container = document.querySelector<HTMLDivElement>("#anti-bot-refresh-clearance");
        if (!container) {
          return;
        }

        // Clear the container
        container.innerHTML = "";

        renderTurnstileWidget(container, {
          "sitekey": REFRESH_CLEARANCE_TURNSTILE_SITE_KEY,
          "error-callback": function (e) {
            setIsRefreshingClearance(false);
            reject(e);
          },
          "callback": function (_, preClearanceObtained) {
            if (preClearanceObtained) {
              // The visitor successfully solved the challenge on the page.
              setIsRefreshingClearance(false);
              resolve(void 0);
            } else {
              reject(new Error("Unable to obtain pre-clearance"));
            }
          },
        });
      });

      currentRefreshingClearancePromiseRef.current = renderChallengeAndWaitForResolutionPromise;

      await renderChallengeAndWaitForResolutionPromise;

      currentRefreshingClearancePromiseRef.current = null;
    };

    const refreshClearanceHook = async (response: Response, fetcher: () => Promise<Response>) => {
      if (response.headers.has("cf-mitigated") && response.headers.get("cf-mitigated") === "challenge") {
        // Ensure only one refresh clearance is done at a time
        if (currentRefreshingClearancePromiseRef.current) {
          await currentRefreshingClearancePromiseRef.current;
        } else {
          await triggerRefreshClearance();
        }

        // Replay the original fetch request, this time it will have the cf_clearance Cookie
        response = await fetcher();
      }
      return response;
    };

    api.addPostRequestHook(refreshClearanceHook);

    return () => {
      api.removePostRequestHook(refreshClearanceHook);
    };
  }, []);
};
